// DailyTotals component
import {
  Card, Stack, Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FC, useMemo } from 'react';
import { AnalyticItem } from '@mgm/common/components';
import ScrollBar from '@mgm/common/components/ScrollBar';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getAttributedOrders } from '../../../../store/orders/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TDorLocationMetric } from '../../../../types';

type TDorStats = {
  data: Array<TDorLocationMetric>;
};

const DorStats: FC<TDorStats> = ({ data }) => {
  const { t } = useTranslation('orders');

  const getCurrency = useAppSelector(getCurrencyByCode);
  const orders = useAppSelector(getAttributedOrders);

  const statistics = useMemo(() => {
    const totals = data?.reduce(
      (acc, item) => {
        acc.totalConversionRate += parseFloat(item.sales.conversion_rate) || 0;
        acc.totalAtv += parseFloat(item.sales.atv) || 0;
        return acc;
      },
      { totalConversionRate: 0, totalAtv: 0 },
    );

    const footTraffic = data?.filter((m) => m.footTraffic?.count > 0)?.length;

    const highestTransaction = orders?.length
      ? Math.max(...orders.map((order) => Number(order.summary.netSales) || 0))
      : 0;

    const avgConversionRate = footTraffic
      ? ((totals.totalConversionRate / footTraffic) * 100).toFixed(2)
      : 0;

    return {
      avgConversionRate,
      avgTransaction: footTraffic ? (totals.totalAtv / footTraffic).toFixed(2) : 0,
      highestTransaction,
    };
  }, [data, orders]);

  return (
    <Card
      sx={{
        height: '88px',
      }}
    >
      <ScrollBar>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          sx={{ p: 2 }}
        >
          <AnalyticItem
            title={t('avgConversion')}
            percent={100}
            price={`${statistics.avgConversionRate}%`}
            icon="ph:coins-light"
            color="#36B37E"
          />
          <AnalyticItem
            title={t('avgTransaction')}
            percent={100}
            price={statistics.avgTransaction || 0}
            icon="icon-park-outline:transaction"
            color="rgb(255, 171, 0)"
          />
          <AnalyticItem
            title={t('highestTransaction')}
            percent={100}
            price={`${(statistics.highestTransaction || 0)?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${getCurrency().mark}`}
            icon="ic:outline-emoji-events"
            color="rgb(0, 184, 217)"
          />
        </Stack>
      </ScrollBar>
    </Card>
  );
};

export {
  DorStats,
};
